/**
 * Toon post-processing chain: scene → bloom → grade + tone map + sRGB, in as few full-resolution
 * passes as possible (the game is fill-rate / bandwidth bound at device pixel ratio 2).
 *
 * Same image as the former EffectComposer chain (RenderPass → UnrealBloomPass → grade ShaderPass
 * → OutputPass), but:
 *  - only the scene target is multisampled; the post passes never write into an MSAA target
 *    (the composer's ping-pong targets were both MSAA, so the bloom blend and the grade each paid
 *    a 4× MSAA write plus a resolve at full resolution);
 *  - the bloom's final additive blend (dst += bloom.rgb × bloom.a, UnrealBloomPass's blend
 *    material) moves into the final pass, which also applies the grade, the tone mapping and the
 *    sRGB transfer (the old grade and output passes): one full-resolution pass to the screen.
 */
import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { perf } from '../../debug/perf';

const BLUR_X = new THREE.Vector2(1, 0);
const BLUR_Y = new THREE.Vector2(0, 1);

/** UnrealBloomPass without its final blend: the bloom stays in `output` for the final pass. */
class BloomStage extends UnrealBloomPass {
  /** The high-pass shader got its NaN / Inf guard. */
  private sanitized = false;
  /** Half-resolution bloom (the composite of every mip), valid after `renderBloom`. */
  get output(): THREE.Texture {
    return this.renderTargetsHorizontal[0]!.texture;
  }

  renderBloom(renderer: THREE.WebGLRenderer, input: THREE.Texture): void {
    const self = this as unknown as {
      _oldClearColor: THREE.Color;
      oldClearAlpha: number;
      fsQuad: FullScreenQuad;
      highPassUniforms: Record<string, THREE.IUniform>;
      materialHighPassFilter: THREE.Material;
      separableBlurMaterials: THREE.ShaderMaterial[];
      compositeMaterial: THREE.ShaderMaterial;
      renderTargetBright: THREE.WebGLRenderTarget;
    };
    renderer.getClearColor(self._oldClearColor);
    self.oldClearAlpha = renderer.getClearAlpha();
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setClearColor(this.clearColor, 0);
    // 1. Luminosity high pass (half resolution). One NaN / Inf pixel in the scene (a bad additive
    // shader) would smear across whole blur mips as black tiles: the high pass zeroes them.
    if (!this.sanitized) {
      this.sanitized = true;
      const hp = self.materialHighPassFilter as THREE.ShaderMaterial;
      const fs = hp.fragmentShader;
      const end = fs.lastIndexOf('}');
      if (end > 0) {
        hp.fragmentShader = `${fs.slice(0, end)}\n  gl_FragColor = (any(isnan(gl_FragColor)) || any(isinf(gl_FragColor))) ? vec4(0.0) : min(gl_FragColor, vec4(64.0));\n}`;
        hp.needsUpdate = true;
      }
    }
    self.highPassUniforms['tDiffuse']!.value = input;
    self.highPassUniforms['luminosityThreshold']!.value = this.threshold;
    self.fsQuad.material = self.materialHighPassFilter;
    renderer.setRenderTarget(self.renderTargetBright);
    renderer.clear();
    self.fsQuad.render(renderer);
    // 2. Blur each mip (horizontal, then vertical).
    let src = self.renderTargetBright;
    for (let i = 0; i < this.nMips; i++) {
      const m = self.separableBlurMaterials[i]!;
      self.fsQuad.material = m;
      m.uniforms['colorTexture']!.value = src.texture;
      m.uniforms['direction']!.value = BLUR_X;
      renderer.setRenderTarget(this.renderTargetsHorizontal[i]!);
      renderer.clear();
      self.fsQuad.render(renderer);
      m.uniforms['colorTexture']!.value = this.renderTargetsHorizontal[i]!.texture;
      m.uniforms['direction']!.value = BLUR_Y;
      renderer.setRenderTarget(this.renderTargetsVertical[i]!);
      renderer.clear();
      self.fsQuad.render(renderer);
      src = this.renderTargetsVertical[i]!;
    }
    // 3. Composite the mips into renderTargetsHorizontal[0] (the final blend is the final pass's).
    self.fsQuad.material = self.compositeMaterial;
    self.compositeMaterial.uniforms['bloomStrength']!.value = this.strength;
    self.compositeMaterial.uniforms['bloomRadius']!.value = this.radius;
    self.compositeMaterial.uniforms['bloomTintColors']!.value = this.bloomTintColors;
    renderer.setRenderTarget(this.renderTargetsHorizontal[0]!);
    renderer.clear();
    self.fsQuad.render(renderer);
    renderer.setClearColor(self._oldClearColor, self.oldClearAlpha);
    renderer.autoClear = oldAutoClear;
  }
}

/** Grade uniforms (the toon renderer drives uLift / uPunch / uHurt). */
export interface GradeUniforms {
  uSat: THREE.IUniform<number>;
  uGain: THREE.IUniform<THREE.Vector3>;
  uLift: THREE.IUniform<THREE.Vector3>;
  uVig: THREE.IUniform<number>;
  uPunch: THREE.IUniform<number>;
  uHurt: THREE.IUniform<number>;
}

const FINAL_VERT = `precision highp float; uniform mat4 modelViewMatrix; uniform mat4 projectionMatrix; attribute vec3 position; attribute vec2 uv;
  varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const FINAL_FRAG = `precision highp float;
  uniform sampler2D tScene; uniform sampler2D tBloom; uniform float uBloomOn;
  uniform float uSat; uniform vec3 uGain; uniform vec3 uLift; uniform float uVig; uniform float uPunch; uniform float uHurt; varying vec2 vUv;
  #include <tonemapping_pars_fragment>
  #include <colorspace_pars_fragment>
  void main(){
    vec4 c = texture2D(tScene, vUv);
    // UnrealBloomPass's additive blend (SRC_ALPHA, ONE).
    vec4 b = texture2D(tBloom, vUv);
    c.rgb += b.rgb * b.a * uBloomOn;
    vec3 col = c.rgb;
    // Grade (was its own pass).
    float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
    col = mix(vec3(l), col, uSat + 0.25 * uPunch) * uGain;
    col += uLift * (1.0 - clamp(l, 0.0, 1.0));
    vec2 d = vUv - 0.5; float r2 = dot(d, d) * 2.0;
    // Impact frame: contrast around mid-grey and a slightly deeper vignette (never brighter overall).
    col = max(vec3(0.0), (col - 0.16) * (1.0 + 0.3 * uPunch) + 0.16 * (1.0 - 0.12 * uPunch));
    col *= 1.0 - (uVig + 0.22 * uPunch) * r2;
    // Hurt: the screen edges darken toward a deep red (dull, so danger orange still reads).
    float e = smoothstep(0.32, 1.0, r2) * uHurt;
    col = mix(col, col * vec3(0.66, 0.24, 0.22) + vec3(0.04, 0.0, 0.004), e * 0.75);
    gl_FragColor = vec4(max(col, 0.0), c.a);
    // Output (was OutputPass): tone mapping, then the sRGB transfer.
    #ifdef NEUTRAL_TONE_MAPPING
      gl_FragColor.rgb = NeutralToneMapping(gl_FragColor.rgb);
    #elif defined(ACES_FILMIC_TONE_MAPPING)
      gl_FragColor.rgb = ACESFilmicToneMapping(gl_FragColor.rgb);
    #elif defined(AGX_TONE_MAPPING)
      gl_FragColor.rgb = AgXToneMapping(gl_FragColor.rgb);
    #elif defined(REINHARD_TONE_MAPPING)
      gl_FragColor.rgb = ReinhardToneMapping(gl_FragColor.rgb);
    #elif defined(CINEON_TONE_MAPPING)
      gl_FragColor.rgb = CineonToneMapping(gl_FragColor.rgb);
    #elif defined(LINEAR_TONE_MAPPING)
      gl_FragColor.rgb = LinearToneMapping(gl_FragColor.rgb);
    #endif
    #ifdef SRGB_TRANSFER
      gl_FragColor = sRGBTransferOETF(gl_FragColor);
    #endif
  }`;

function toneDefines(gl: THREE.WebGLRenderer): Record<string, string> {
  const d: Record<string, string> = {};
  if (THREE.ColorManagement.getTransfer(gl.outputColorSpace) === THREE.SRGBTransfer) d['SRGB_TRANSFER'] = '';
  const t = gl.toneMapping;
  if (t === THREE.NeutralToneMapping) d['NEUTRAL_TONE_MAPPING'] = '';
  else if (t === THREE.ACESFilmicToneMapping) d['ACES_FILMIC_TONE_MAPPING'] = '';
  else if (t === THREE.AgXToneMapping) d['AGX_TONE_MAPPING'] = '';
  else if (t === THREE.ReinhardToneMapping) d['REINHARD_TONE_MAPPING'] = '';
  else if (t === THREE.CineonToneMapping) d['CINEON_TONE_MAPPING'] = '';
  else if (t === THREE.LinearToneMapping) d['LINEAR_TONE_MAPPING'] = '';
  return d;
}

export class ToonPost {
  readonly bloom: BloomStage;
  readonly grade: GradeUniforms;
  /** Bloom on (settings.bloom && the quality profile allows it). */
  bloomEnabled = true;
  private sceneRT: THREE.WebGLRenderTarget;
  private readonly finalMat: THREE.RawShaderMaterial;
  private readonly quad: FullScreenQuad;
  private toneKey = '';
  private w = 1;
  private h = 1;

  constructor(
    private readonly gl: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
    msaa: number,
    grade: GradeUniforms,
  ) {
    this.grade = grade;
    this.sceneRT = ToonPost.target(1, 1, msaa);
    this.bloom = new BloomStage(new THREE.Vector2(1, 1), 0.5, 0.4, 0.85);
    // Raw: a ShaderMaterial drawn to the screen would get three's own tone-mapping prefix too.
    this.finalMat = new THREE.RawShaderMaterial({
      uniforms: { tScene: { value: null }, tBloom: { value: null }, uBloomOn: { value: 1 }, toneMappingExposure: { value: 1 }, ...grade },
      vertexShader: FINAL_VERT,
      fragmentShader: FINAL_FRAG,
      depthTest: false,
      depthWrite: false,
    });
    this.quad = new FullScreenQuad(this.finalMat);
  }

  private static target(w: number, h: number, samples: number): THREE.WebGLRenderTarget {
    return new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, samples });
  }

  /** Drawing-buffer size in device pixels (CSS size × pixel ratio). */
  setSize(cssW: number, cssH: number, pixelRatio: number): void {
    this.w = Math.max(1, Math.floor(cssW * pixelRatio));
    this.h = Math.max(1, Math.floor(cssH * pixelRatio));
    this.sceneRT.setSize(this.w, this.h);
    this.bloom.setSize(this.w, this.h);
  }

  /** New MSAA sample count for the scene target (quality change). */
  setMsaa(samples: number): void {
    if (this.sceneRT.samples === samples) return;
    this.sceneRT.dispose();
    this.sceneRT = ToonPost.target(this.w, this.h, samples);
  }

  render(): void {
    const gl = this.gl;
    perf.begin('r.gl.scene');
    gl.setRenderTarget(this.sceneRT);
    gl.render(this.scene, this.camera);
    perf.end('r.gl.scene');
    if (this.bloomEnabled) {
      perf.begin('r.gl.bloom');
      this.bloom.renderBloom(gl, this.sceneRT.texture);
      perf.end('r.gl.bloom');
    }
    perf.begin('r.gl.final');
    const key = `${gl.toneMapping}|${gl.outputColorSpace}`;
    if (key !== this.toneKey) {
      this.toneKey = key;
      this.finalMat.defines = toneDefines(gl);
      this.finalMat.needsUpdate = true;
    }
    const u = this.finalMat.uniforms;
    u['tScene']!.value = this.sceneRT.texture;
    u['tBloom']!.value = this.bloom.output;
    u['uBloomOn']!.value = this.bloomEnabled ? 1 : 0;
    u['toneMappingExposure']!.value = gl.toneMappingExposure;
    gl.setRenderTarget(null);
    this.quad.render(gl);
    perf.end('r.gl.final');
  }

  dispose(): void {
    this.sceneRT.dispose();
    this.bloom.dispose();
    this.finalMat.dispose();
    this.quad.dispose();
  }
}
